import { yupResolver } from "@hookform/resolvers/yup";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { Link } from "@omni/ui/components/Link/link.component";
import { cn } from "@omni/utils/styles";
import { useCallback, useMemo } from "react";
import { FormProvider, Resolver } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { AddressValidationProvider } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { AddressFormBlockContextProvider } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.context";
import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { DateField } from "@/components/Form/RHF/FormFields/DateField/dateField.component";
import { MultiTextField } from "@/components/Form/RHF/FormFields/MultiTextField/multiTextField.component";
import { TextFieldArray } from "@/components/Form/RHF/FormFields/MultiTextField/textFieldArray.component";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import {
    COUNTRY_BE,
    getEmptyPrefillAddress,
    toBePrefillAddress,
    toNonBePrefillAddress
} from "@/components/WithdrawalForm/helpers/address.helpers";
import { AddressPartial } from "@/components/WithdrawalForm/partials/addressPartial";
import {
    WITHDRAWAL_FAILLURE_MESSAGE_ID,
    WITHDRAWAL_FORM_ID,
    WITHDRAWAL_SUCCESS_MESSAGE_ID
} from "@/components/WithdrawalForm/withdrawalForm.config";
import {
    WithdrawalFormInput,
    WithdrawalFormSchema,
    WithdrawalFormValues
} from "@/components/WithdrawalForm/withdrawalFrom.schema";
import * as AddressBookModule from "@/redux/modules/api/data/addressbook/addressbook.module";
import * as AddressBookSelectors from "@/redux/modules/api/data/addressbook/addressbook.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { useReduxApiState } from "@/utils/hooks/useReduxApiState.hook";

type Props = {
    className?: string;
    termsOfServiceLink: string;
    submitting: boolean;
    onSubmit: (values: WithdrawalFormValues) => Promise<void>;
};

export const WithdrawalFormComponent = ({
    className,
    submitting,
    termsOfServiceLink,
    onSubmit
}: Props) => {
    const { t } = useGranite();

    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const { state: customerPromise } = useReduxApiState(
        CustomerSelectors.isLoading,
        CustomerSelectors.getCustomer,
        CustomerModule.load()
    );

    const { state: addressBookPromise } = useReduxApiState(
        AddressBookSelectors.isLoading,
        AddressBookSelectors.getAddressBook,
        AddressBookModule.load()
    );

    const defaultAddressPromise = useMemo(() => {
        return addressBookPromise
            .then((addressBook) => {
                return (
                    addressBook?.addresses?.find(
                        (a) => a.defaultDeliveryAddress
                    ) ??
                    addressBook?.addresses?.find(
                        (a) => a.defaultPaymentAddress
                    ) ??
                    addressBook?.addresses?.at(0)
                );
            })
            .then((address) => {
                if (!address) {
                    return getEmptyPrefillAddress();
                } else if (address.country === COUNTRY_BE) {
                    return toBePrefillAddress(address);
                } else {
                    return toNonBePrefillAddress(address);
                }
            });
    }, [addressBookPromise]);

    const asyncDefaultValues = useCallback(async () => {
        const [customer, address] = await Promise.all([
            customerPromise,
            defaultAddressPromise
        ]);

        return {
            lastName: customer?.lastName ?? "",
            firstName: customer?.firstName ?? "",
            email: customer?.email ?? "",
            phone: customer?.phone ?? "",
            phonePrefix: customer?.phonePrefix || "+32",
            orderNumber: "",
            withdrawalProducts: [],
            withdrawalReason: "",
            agreedToTermsAndConditions: false,
            receivedOn: "",
            orderDate: "",
            address: address
        } satisfies WithdrawalFormInput;
    }, [customerPromise, defaultAddressPromise]);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: asyncDefaultValues,
        resolver: yupResolver(WithdrawalFormSchema) as Resolver<
            WithdrawalFormInput,
            any,
            WithdrawalFormValues
        >,
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const handleOnFormSubmit = async (values: WithdrawalFormValues) => {
        return onSubmit(values).then(() => methods.reset());
    };

    return (
        <>
            <Messages messageid={WITHDRAWAL_SUCCESS_MESSAGE_ID} />
            <Messages messageid={WITHDRAWAL_FAILLURE_MESSAGE_ID} />
            <FormInfoProvider
                formId={WITHDRAWAL_FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => {
                    const disabled =
                        !initialized || methods.formState.isLoading;
                    const country =
                        methods.getValues("address.country") || COUNTRY_BE;

                    return (
                        <FormProvider {...methods}>
                            <form
                                id={WITHDRAWAL_FORM_ID}
                                className={className}
                                onSubmit={methods.handleSubmit(
                                    handleOnFormSubmit
                                )}
                            >
                                <Row>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField<WithdrawalFormValues>
                                            name={"lastName"}
                                            label={t(
                                                "general.form.lastName.label"
                                            )}
                                            required={true}
                                            autoComplete={"family-name"}
                                            placeholder={t(
                                                "general.form.lastName.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField<WithdrawalFormValues>
                                            name={"firstName"}
                                            label={t(
                                                "general.form.firstName.label"
                                            )}
                                            required={true}
                                            autoComplete={"given-name"}
                                            placeholder={t(
                                                "general.form.firstName.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField<WithdrawalFormValues>
                                            name={"email"}
                                            label={t(
                                                "general.form.email.label"
                                            )}
                                            required={true}
                                            autoComplete={"email"}
                                            placeholder={t(
                                                "general.form.email.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <PhoneField<WithdrawalFormValues>
                                            autoComplete={"tel-national"}
                                            label={t(
                                                "general.form.phoneNumber.label"
                                            )}
                                            placeholder={t(
                                                "general.form.phoneNumber.placeholder"
                                            )}
                                            required={false}
                                            phonenumberPrefixes={
                                                phonenumberPrefixes
                                            }
                                            disabled={disabled}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <AddressValidationProvider>
                                            <AddressFormBlockContextProvider
                                                addressStatus={
                                                    AddressValidationStatus.Unknown
                                                }
                                                addressType={
                                                    AddressType.Default
                                                }
                                            >
                                                <AddressPartial
                                                    disabled={disabled}
                                                    country={country}
                                                />
                                            </AddressFormBlockContextProvider>
                                        </AddressValidationProvider>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <DateField<WithdrawalFormValues>
                                            name={"orderDate"}
                                            label={t(
                                                "form.withdrawal.orderDate.label"
                                            )}
                                            required={true}
                                            placeholder={t(
                                                "form.withdrawal.orderDate.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                    <Col
                                        xs={12}
                                        md={6}
                                    >
                                        <DateField<WithdrawalFormValues>
                                            name={"receivedOn"}
                                            label={t(
                                                "form.withdrawal.receivedOn.label"
                                            )}
                                            required={false}
                                            placeholder={t(
                                                "form.withdrawal.receivedOn.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <TextField<WithdrawalFormValues>
                                            name={"orderNumber"}
                                            label={t(
                                                "form.withdrawal.orderNumber.label"
                                            )}
                                            required={true}
                                            placeholder={t(
                                                "form.withdrawal.orderNumber.placeholder"
                                            )}
                                            disabled={disabled}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <MultiTextField<WithdrawalFormValues>
                                            name={"withdrawalProducts"}
                                            label={t(
                                                "form.withdrawal.withdrawalProducts.label"
                                            )}
                                            required={false}
                                            disabled={disabled}
                                        >
                                            {(props) => (
                                                <TextFieldArray<WithdrawalFormValues>
                                                    {...props}
                                                    placeholder={t(
                                                        "form.withdrawal.withdrawalProducts.placeholder"
                                                    )}
                                                    onAdd={(append) =>
                                                        append({ value: "" })
                                                    }
                                                    addFieldLabel={t(
                                                        "form.withdrawal.withdrawalProducts.addField"
                                                    )}
                                                />
                                            )}
                                        </MultiTextField>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <TextAreaField<WithdrawalFormValues>
                                            name={"withdrawalReason"}
                                            label={t(
                                                "form.withdrawal.withdrawalReason.label"
                                            )}
                                            required={false}
                                            placeholder={t(
                                                "form.withdrawal.withdrawalReason.placeholder"
                                            )}
                                            maxLength={1500}
                                            disabled={disabled}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <CheckBoxField<WithdrawalFormValues>
                                            name={"agreedToTermsAndConditions"}
                                            required={true}
                                            disabled={disabled}
                                            label={t(
                                                "form.validation.input.termsAndConditions.required"
                                            )}
                                        >
                                            {() => (
                                                <>
                                                    {t(
                                                        "general.form.agreedToTermsAndConditions.label"
                                                    )}
                                                    <Link
                                                        color={"link"}
                                                        className={cn(
                                                            "tw:p-0 tw:ml-1"
                                                        )}
                                                        href={
                                                            termsOfServiceLink
                                                        }
                                                    >
                                                        {t(
                                                            "general.form.agreedToTermsAndConditions.document"
                                                        )}
                                                    </Link>
                                                </>
                                            )}
                                        </CheckBoxField>
                                    </Col>
                                </Row>
                                <Button
                                    type={"submit"}
                                    loading={submitting}
                                    disabled={disabled}
                                    className={cn("tw:mt-4")}
                                >
                                    {t("form.withdrawal.submit")}
                                </Button>
                            </form>
                        </FormProvider>
                    );
                }}
            </FormInfoProvider>
        </>
    );
};
